import { PriceChangeRequestAction, Request } from '../../../codegen/graphql';

type QuickActionRequiredOutcome =
    | 'Approved'
    | 'Declined'
    | 'ApprovedWithTemporaryPrice';

export type BatchQuickActionVerb = 'approve' | 'interim' | 'decline';

export function resolvePriceChangeQuickActionTarget(
    request: Request,
    responsibleUserIds: string[],
): PriceChangeRequestAction | null {
    const actions = (request.priceChangeRequestActions ??
        []) as PriceChangeRequestAction[];

    if (!actions.length) {
        return null;
    }

    const responsibleAndPending = actions.find(
        action =>
            action.responsibleUser?.id &&
            responsibleUserIds.includes(action.responsibleUser.id) &&
            !action.completed,
    );

    return responsibleAndPending?.id ? responsibleAndPending : null;
}

export function isQuickActionOutcomeAllowed(
    target: PriceChangeRequestAction | null,
    outcome: QuickActionRequiredOutcome,
): boolean {
    if (!target) {
        return false;
    }
    return (
        !target.completed && (target.possibleOutcomes ?? []).includes(outcome)
    );
}

export function resolveQuickActionSubmitCapability(
    request: Request,
    verb: BatchQuickActionVerb,
    responsibleUserIds: string[],
    isDivisionManager: boolean,
): { actionId: string | null; canSubmit: boolean } {
    const target = resolvePriceChangeQuickActionTarget(
        request,
        responsibleUserIds,
    );
    const actionId = target?.id ?? null;

    if (!actionId) {
        return { actionId: null, canSubmit: false };
    }

    let canSubmit = false;
    if (verb === 'decline') {
        canSubmit = isQuickActionOutcomeAllowed(target, 'Declined');
    } else if (verb === 'interim') {
        canSubmit =
            !isDivisionManager &&
            isQuickActionOutcomeAllowed(target, 'ApprovedWithTemporaryPrice');
    } else {
        canSubmit = isQuickActionOutcomeAllowed(target, 'Approved');
    }

    return { actionId, canSubmit };
}
